import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";
import { useUser } from "../../../store/useUser";
import { useSetHelpTopic } from "../../../store/useHelpTopics";
import { EditCoordinateCare } from "./modals/EditCoordinateCare";
import { Card, CardContent } from "@/components/ui/card";
import { AddEntryModal, type EntryType } from "../add_entry";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { Plus, Edit, Trash, ClipboardList, Clock, AlertTriangle, Search, RefreshCw, ChevronDown } from "lucide-react";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { TabHeader } from "../shared/components/TabHeader";
import { TabSpinner } from "../shared/components/TabSpinner";
import { TabToolbar } from "../shared/components/TabToolbar";
import { TabTable } from "../shared/components/TabTable";
import { EmptyState } from "@/components/ui/EmptyState";
import { hasAnyPolicy } from "@/utils/policies";

// TypeScript interfaces
interface CoordinatedCareNote {
  id: string;
  serviceType: string;
  placeOfService: string;
  procedurelength: string;
  noteEntryDate: string;
  procedurenote: string;
  procedureEnteredBy: string;
  entrydate: string;
  entryby: string;
}

interface CoordinatedCareProps {
  odrNumber: number;
  programNumber: number;
}

const GET_CALAIM_DATA = gql`
  query GetCalAIMData($odrNumber: Int!) {
    getParticipantByODRNumber(odrnumber: $odrNumber) {
      ssn
      cin
      mrun
      fin
      cii
    }
  }
`;

const GET_COORDINATED_CARE_NOTES = gql`
  query GetCoordinatedCareNotes($odrnumber: String!) {
    coordinatedCareNotes(odrnumber: $odrnumber) {
      id
      serviceType
      placeOfService
      procedurelength
      noteEntryDate
      procedurenote
      procedureEnteredBy
      entrydate
      entryby
    }
  }
`;


const DELETE_COORDINATED_CARE_NOTE = gql`
  mutation DeleteCoordinatedCareNote($noteId: Int!) {
    deleteCoordinatedCareNote(noteId: $noteId) {
      message
    }
  }
`;

export const CoordinatedCare: React.FC<CoordinatedCareProps> = ({ odrNumber, programNumber }) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCoordinatedCareNote, setSelectedCoordinatedCareNote] = useState<CoordinatedCareNote | null>(null);
  const [newNoteModalOpen, setNewNoteModalOpen] = useState(false);
  const [isNoteMenuOpen, setIsNoteMenuOpen] = useState(false);
  const [newNoteEntryType, setNewNoteEntryType] = useState<EntryType | undefined>(undefined);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState<string>("all");
  const [sortAsc, setSortAsc] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState<number | null>(null);
  const [showFullNotes, setShowFullNotes] = useState(false);
  const { user } = useUser();
  const coordinatedCareWritePolicies = ['program:participant:coordinatedcare~full', 'tools:billing:access~full'];
  const canCreateNote = hasAnyPolicy(user, coordinatedCareWritePolicies);
  const canEditNote = hasAnyPolicy(user, coordinatedCareWritePolicies);
  const canDeleteNote = hasAnyPolicy(user, coordinatedCareWritePolicies);

  const { loading, error, data, refetch } = useQuery(GET_CALAIM_DATA, {
    variables: { odrNumber },
    skip: !odrNumber,
  });

  const { loading: loadingNotes, error: notesError, data: notesData, refetch: refetchNotes } = useQuery(GET_COORDINATED_CARE_NOTES, {
    variables: { odrnumber: odrNumber.toString() },
    skip: !odrNumber,
  });

  const [deleteCoordinatedCareNote, { loading: deleteLoading }] = useMutation(DELETE_COORDINATED_CARE_NOTE);

  const handleEditClick = (coordinatedCareNote: CoordinatedCareNote) => {
    setSelectedCoordinatedCareNote(coordinatedCareNote);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (noteId: number) => {
    setNoteToDelete(noteId);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (noteToDelete === null) return;
    
    try {
      await deleteCoordinatedCareNote({
        variables: {
          noteId: noteToDelete
        }
      });
      refetchNotes();
      setIsDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (error) {
      console.error("Error deleting Coordinated Care note:", error);
    }
  };

  const clearFilters = () => {
    setSearch("");
    setServiceFilter("all");
  };

  const openNewNoteModal = (entryType: EntryType) => {
    setIsNoteMenuOpen(false);
    window.setTimeout(() => {
      setNewNoteEntryType(entryType);
      setNewNoteModalOpen(true);
    }, 0);
  };

  useSetHelpTopic("ParticipantsCoordinatedCare");

  if (loading) return <TabSpinner label="Loading coordinated care..." />;
  
  if (error || !data) return (
    <div className="p-12">
      <EmptyState
        icon={AlertTriangle}
        title="Unable to load care data"
        description="There was an error connecting to the clinical data service."
        action={<Button variant="outline" size="sm" onClick={() => refetch()}>Retry Connection</Button>}
      />
    </div>
  );

  const calaimData = data.getParticipantByODRNumber;
  // Ensure coordinatedCareNotes is always an array by using Array.isArray check
  const coordinatedCareNotes = Array.isArray(notesData?.coordinatedCareNotes) ? notesData.coordinatedCareNotes : [];

  const filteredNotes = coordinatedCareNotes
    .filter((note: CoordinatedCareNote) => {
      const term = search.toLowerCase();
      const matchesText =
        note.procedurenote?.toLowerCase().includes(term) ||
        note.entryby?.toLowerCase().includes(term) ||
        note.serviceType?.toLowerCase().includes(term);
      const matchesService = serviceFilter === "all" || note.serviceType === serviceFilter;
      return matchesText && matchesService;
    })
    .sort((a: CoordinatedCareNote, b: CoordinatedCareNote) => {
      const da = new Date(a.noteEntryDate).getTime();
      const db = new Date(b.noteEntryDate).getTime();
      return sortAsc ? da - db : db - da;
    });

  const noteCellClass = showFullNotes
    ? "text-sm text-gray-800 whitespace-pre-wrap"
    : "text-sm text-gray-800 line-clamp-3 max-w-[520px]";

  let notesContent: React.ReactNode = <TabSpinner label="Loading notes..." />;
  if (notesError) {
    notesContent = (
      <div className="py-20">
        <EmptyState
          icon={AlertTriangle}
          title="Error loading notes"
          description={notesError.message}
          action={<Button variant="outline" size="sm" onClick={() => refetchNotes()}>Try Again</Button>}
        />
      </div>
    );
  }
  if (!loadingNotes && !notesError && filteredNotes.length === 0) {
    notesContent = (
      <div className="py-20">
        <EmptyState
          icon={ClipboardList}
          title={search || serviceFilter !== 'all' ? "No matching notes" : "No care notes found"}
          description={search || serviceFilter !== 'all' ? "Try adjusting your filters or search terms." : "Clinical coordination notes will appear here once recorded."}
          action={search || serviceFilter !== 'all' ? <Button variant="outline" size="sm" onClick={clearFilters}>Clear Filters</Button> : undefined}
        />
      </div>
    );
  }
  if (!loadingNotes && !notesError && filteredNotes.length > 0) {
    notesContent = (
      <TabTable<CoordinatedCareNote>
        data={filteredNotes}
        emptyMessage="No coordinated care notes found."
        columns={[
      { title: "Date", className: "w-[120px]", render: (note) => note.noteEntryDate || "—" },
      { title: "Service", className: "w-[140px]", render: (note) => note.serviceType ? <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-100">{note.serviceType}</Badge> : "—" },
      { title: "Place", className: "w-[140px]", render: (note) => note.placeOfService ? <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-100">{note.placeOfService}</Badge> : "—" },
          { title: "Note", render: (note) => <div className={noteCellClass}>{note.procedurenote || "—"}</div> },
          { title: "Entered By", className: "w-[140px]", render: (note) => note.entryby || "—" },
          { title: "Actions", className: "w-[120px]", render: (note) => (
            canEditNote || canDeleteNote ? (
              <div className="flex items-center justify-end gap-1">
                {canEditNote && (
                  <Button
                    aria-label={`Edit coordinated care note ${note.id}`}
                    data-testid={`coordinated-care-edit-${note.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEditClick(note)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                )}
                {canDeleteNote && (
                  <Button
                    aria-label={`Delete coordinated care note ${note.id}`}
                    data-testid={`coordinated-care-delete-${note.id}`}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeleteClick(parseInt(note.id, 10))}
                  >
                    <Trash className="h-4 w-4 text-red-600" />
                  </Button>
                )}
              </div>
            ) : (
              <span className="text-xs text-gray-400">—</span>
            )
          )},
        ]}
      />
    );
  }

  return (
    <div className="space-y-5 h-full flex flex-col">
      <TabHeader
        icon={ClipboardList}
        title="Coordinated Care"
        subtitle="Care coordination notes and services"
        action={
          canCreateNote ? (
            <DropdownMenu open={isNoteMenuOpen} onOpenChange={setIsNoteMenuOpen}>
              <DropdownMenuTrigger asChild>
                <Button
                  size="sm"
                  className="h-8 px-4 gap-1.5 shadow-sm"
                >
                  <Plus className="h-4 w-4" />
                  Add Note
                  <ChevronDown className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    openNewNoteModal("coordinated_care");
                  }}
                >
                  Coordinated Care Note
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(event) => {
                    event.preventDefault();
                    openNewNoteModal("calaim");
                  }}
                >
                  ECM Note
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null
        }
      />
      <TabToolbar
        left={
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search notes..."
                className="h-8 w-60 pl-8 text-xs"
              />
            </div>
            <Select
              value={serviceFilter}
              onValueChange={setServiceFilter}
            >
              <SelectTrigger className="h-8 w-40 text-xs">
                <SelectValue placeholder="Service type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All services</SelectItem>
                <SelectItem value="Housing">Housing</SelectItem>
                <SelectItem value="Community">Community</SelectItem>
                <SelectItem value="Pre-release">Pre-release</SelectItem>
                <SelectItem value="Psychiatry">Psychiatry</SelectItem>
                <SelectItem value="Nursing">Nursing</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => setSortAsc((prev) => !prev)}
              className="h-8 text-xs font-bold uppercase tracking-wider"
            >
              {sortAsc ? "Oldest first" : "Newest first"}
            </Button>
          </div>
        }
        right={
          <div className="flex items-center gap-2">
            <Checkbox
              id="coordinated-care-show-notes"
              checked={showFullNotes}
              onCheckedChange={(checked) => setShowFullNotes(Boolean(checked))}
            />
            <label
              htmlFor="coordinated-care-show-notes"
              className="text-xs font-bold text-gray-400 uppercase tracking-wider cursor-pointer select-none"
            >
              Show full notes
            </label>
          </div>
        }
      />

      <div className="flex-1 min-h-0 flex flex-col">
        <Card className="flex-1 overflow-hidden rounded-none border-0 shadow-none bg-white">
          <CardContent className="p-0 h-full flex flex-col">
            <div className="border-t border-b border-gray-200 flex-1 min-h-0 overflow-auto">{notesContent}</div>
          </CardContent>
        </Card>
      </div>

      <AddEntryModal
        isOpen={newNoteModalOpen}
        onClose={() => {
          setNewNoteModalOpen(false);
          setNewNoteEntryType(undefined);
        }}
        odrNumber={odrNumber.toString()}
        participantId={programNumber.toString()}
        user={user}
        refetch={refetchNotes}
        allowedEntryTypes={["calaim", "coordinated_care"]}
        initialEntryType={newNoteEntryType}
      />
            
      <EditCoordinateCare
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCoordinatedCareNote(null);
          refetchNotes();
        }}
        coordinatedCareData={selectedCoordinatedCareNote}
        user={user}
      />

      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="bg-white p-4 rounded-lg shadow-xl max-w-md border border-gray-200">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-lg font-bold flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Care Note
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-gray-600 pt-2">
              Are you sure you want to delete this coordinated care note? This action is permanent and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-6 flex items-center justify-between gap-3">
            <AlertDialogCancel className="h-9 text-xs font-bold uppercase tracking-wider border-gray-300" onClick={() => setIsDeleteDialogOpen(false)}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              className="h-9 text-xs font-bold uppercase tracking-wider bg-red-600 hover:bg-red-700 shadow-sm"
              onClick={handleDeleteConfirm} 
              disabled={deleteLoading}
            >
              {deleteLoading ? (
                <span className="flex items-center gap-1.5">
                  <RefreshCw className="h-3.5 w-3.5 animate-spin" />
                  Deleting...
                </span>
              ) : (
                <span className="flex items-center gap-1.5">
                  <Trash className="h-3.5 w-3.5" />
                  Delete Note
                </span>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CoordinatedCare;
